import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  AdminMessage,
  AuditListParams,
  AuditListResponse,
  BanListParams,
  BansListResponse,
  ConversationDetail,
  ConversationListParams,
  ConversationMessagesParams,
  ConversationRegistry,
  ConversationsListResponse,
  MessageListParams,
  MessageOverview,
  MessagesListResponse,
  MessageThreadParams,
  MessageThreadResponse,
  MessagingBan,
  MessageReport,
  Pagination,
  ReportListParams,
  ReportsListResponse,
} from '@/client/types/messaging';

const B = '/api/admin/v2';

export const messagingKeys = {
  all: ['messaging-v2'] as const,
  messages: () => [...messagingKeys.all, 'messages'] as const,
  messageList: (p: object) => [...messagingKeys.messages(), 'list', p] as const,
  messageDetail: (ref: string) => [...messagingKeys.messages(), 'detail', ref] as const,
  messageThread: (p: object) => [...messagingKeys.messages(), 'thread', p] as const,
  overview: () => [...messagingKeys.messages(), 'overview'] as const,
  audit: (p: object) => [...messagingKeys.messages(), 'audit', p] as const,
  blocks: (uid?: number) => [...messagingKeys.messages(), 'blocks', uid] as const,
  export: (p: object) => [...messagingKeys.messages(), 'export', p] as const,

  conversations: () => [...messagingKeys.all, 'conversations'] as const,
  conversationList: (p: object) => [...messagingKeys.conversations(), 'list', p] as const,
  conversationDetail: (key: string) => [...messagingKeys.conversations(), 'detail', key] as const,
  conversationMessages: (key: string, p: object) =>
    [...messagingKeys.conversations(), 'messages', key, p] as const,

  reports: () => [...messagingKeys.all, 'reports'] as const,
  reportList: (p: object) => [...messagingKeys.reports(), 'list', p] as const,
  reportDetail: (rid: string) => [...messagingKeys.reports(), 'detail', rid] as const,

  bans: () => [...messagingKeys.all, 'bans'] as const,
  banList: (p: object) => [...messagingKeys.bans(), 'list', p] as const,
  banDetail: (banid: string) => [...messagingKeys.bans(), 'detail', banid] as const,
};

const qs = (raw: Record<string, unknown>) => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

/** ff-api wraps every payload in `{ status, data }` — always read `.data`. */
const unwrap = async <T>(promise: Promise<APIResponse<T> | T>): Promise<T> => {
  const res = await promise;
  if (res != null && typeof res === 'object' && 'data' in (res as object) && 'status' in (res as object)) {
    return (res as APIResponse<T>).data as T;
  }
  return res as T;
};

const emptyPagination = (): Pagination => ({ total: 0, limit: 0, offset: 0, has_more: false });

const asMessagesList = (raw: unknown): MessagesListResponse => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const messages = (r.messages ?? r.items ?? []) as AdminMessage[];
  const pagination = (r.pagination as Pagination) ?? {
    ...emptyPagination(),
    total: messages.length,
    limit: messages.length,
  };
  return { messages, pagination };
};

const asConversationsList = (raw: unknown): ConversationsListResponse => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const conversations = (r.conversations ??
    r.message_conversations ??
    r.items ??
    []) as ConversationRegistry[];
  const pagination = (r.pagination as Pagination) ?? {
    ...emptyPagination(),
    total: conversations.length,
    limit: conversations.length,
  };
  return { conversations, pagination };
};

const asReportsList = (raw: unknown): ReportsListResponse => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const reports = (r.reports ?? r.message_reports ?? r.items ?? []) as MessageReport[];
  const pagination = (r.pagination as Pagination) ?? {
    ...emptyPagination(),
    total: reports.length,
    limit: reports.length,
  };
  return { reports, pagination };
};

const asBansList = (raw: unknown): BansListResponse => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const bans = (r.bans ?? r.messaging_bans ?? r.items ?? []) as MessagingBan[];
  const pagination = (r.pagination as Pagination) ?? {
    ...emptyPagination(),
    total: bans.length,
    limit: bans.length,
  };
  return { bans, pagination };
};

// ─── Messages ─────────────────────────────────────────────────────────────────

export const useMessageListQuery = (params: MessageListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.messageList(params),
    queryFn: withErrorHandling(async (): Promise<MessagesListResponse> => {
      const data = await unwrap(
        request<APIResponse<MessagesListResponse>>(
          `${B}/messages${qs(params as Record<string, unknown>)}`,
        ),
      );
      return asMessagesList(data);
    }, 'Failed to fetch messages'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useMessageOverviewQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.overview(),
    queryFn: withErrorHandling(async (): Promise<MessageOverview> => {
      return unwrap(request<APIResponse<MessageOverview>>(`${B}/messages/overview`));
    }, 'Failed to fetch messaging overview'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useMessageDetailQuery = (ref: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.messageDetail(ref ?? ''),
    queryFn: withErrorHandling(async (): Promise<AdminMessage> => {
      return unwrap(
        request<APIResponse<AdminMessage>>(`${B}/messages/${encodeURIComponent(ref!)}`),
      );
    }, 'Failed to fetch message'),
    enabled: enabled && !!ref,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Full back-and-forth between two parties — preferred when conversation_key is null. */
export const useMessageThreadQuery = (
  params: MessageThreadParams | null,
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  const hasParties =
    !!params &&
    (params.a_uid != null || params.a_oid != null) &&
    (params.b_uid != null || params.b_oid != null);
  return useQuery({
    queryKey: messagingKeys.messageThread(params ?? {}),
    queryFn: withErrorHandling(async (): Promise<MessageThreadResponse> => {
      const data = await unwrap(
        request<APIResponse<MessageThreadResponse>>(
          `${B}/messages/thread${qs((params ?? {}) as Record<string, unknown>)}`,
        ),
      );
      const r = (data ?? {}) as MessageThreadResponse & Record<string, unknown>;
      return {
        party_a: r.party_a,
        party_b: r.party_b,
        entity: r.entity ?? null,
        messages: (r.messages ?? []) as AdminMessage[],
        pagination: r.pagination ?? {
          total: (r.messages as AdminMessage[] | undefined)?.length ?? 0,
          limit: 50,
          offset: 0,
        },
      };
    }, 'Failed to fetch message thread'),
    enabled: enabled && hasParties,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useMessageAuditQuery = (params: AuditListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.audit(params),
    queryFn: withErrorHandling(async (): Promise<AuditListResponse> => {
      return unwrap(
        request<APIResponse<AuditListResponse>>(
          `${B}/messages/audit${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch message audit log'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useMessageExportQuery = (
  params: { uid?: number; oid?: number; limit?: number },
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.export(params),
    queryFn: withErrorHandling(async (): Promise<MessagesListResponse> => {
      const data = await unwrap(
        request<APIResponse<MessagesListResponse>>(`${B}/messages/export${qs(params)}`),
      );
      return asMessagesList(data);
    }, 'Failed to export messages'),
    enabled: enabled && (params.uid != null || params.oid != null),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useMessageBlocksQuery = (uid?: number, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.blocks(uid),
    queryFn: withErrorHandling(async () => {
      return unwrap(request(`${B}/messages/blocks${qs({ uid })}`));
    }, 'Failed to fetch blocklists'),
    enabled: enabled && uid != null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Conversations ────────────────────────────────────────────────────────────

export const useConversationListQuery = (params: ConversationListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.conversationList(params),
    queryFn: withErrorHandling(async (): Promise<ConversationsListResponse> => {
      const data = await unwrap(
        request<APIResponse<ConversationsListResponse>>(
          `${B}/message-conversations${qs(params as Record<string, unknown>)}`,
        ),
      );
      return asConversationsList(data);
    }, 'Failed to fetch conversations'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useConversationDetailQuery = (key: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.conversationDetail(key ?? ''),
    queryFn: withErrorHandling(async (): Promise<ConversationDetail> => {
      return unwrap(
        request<APIResponse<ConversationDetail>>(
          `${B}/message-conversations/${encodeURIComponent(key!)}`,
        ),
      );
    }, 'Failed to fetch conversation'),
    enabled: enabled && !!key,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useConversationMessagesQuery = (
  key: string | null,
  params: ConversationMessagesParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.conversationMessages(key ?? '', params),
    queryFn: withErrorHandling(async (): Promise<MessagesListResponse> => {
      const data = await unwrap(
        request<APIResponse<MessagesListResponse>>(
          `${B}/message-conversations/${encodeURIComponent(key!)}/messages${qs(
            params as Record<string, unknown>,
          )}`,
        ),
      );
      return asMessagesList(data);
    }, 'Failed to fetch conversation messages'),
    enabled: enabled && !!key,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export const useReportListQuery = (params: ReportListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.reportList(params),
    queryFn: withErrorHandling(async (): Promise<ReportsListResponse> => {
      const data = await unwrap(
        request<APIResponse<ReportsListResponse>>(
          `${B}/message-reports${qs(params as Record<string, unknown>)}`,
        ),
      );
      return asReportsList(data);
    }, 'Failed to fetch message reports'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useReportDetailQuery = (rid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.reportDetail(rid ?? ''),
    queryFn: withErrorHandling(async (): Promise<MessageReport> => {
      return unwrap(request<APIResponse<MessageReport>>(`${B}/message-reports/${rid}`));
    }, 'Failed to fetch report'),
    enabled: enabled && !!rid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Bans ─────────────────────────────────────────────────────────────────────

export const useBanListQuery = (params: BanListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.banList(params),
    queryFn: withErrorHandling(async (): Promise<BansListResponse> => {
      const data = await unwrap(
        request<APIResponse<BansListResponse>>(
          `${B}/message-bans${qs(params as Record<string, unknown>)}`,
        ),
      );
      return asBansList(data);
    }, 'Failed to fetch messaging bans'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useBanDetailQuery = (banid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: messagingKeys.banDetail(banid ?? ''),
    queryFn: withErrorHandling(async (): Promise<MessagingBan> => {
      return unwrap(request<APIResponse<MessagingBan>>(`${B}/message-bans/${banid}`));
    }, 'Failed to fetch ban'),
    enabled: enabled && !!banid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
